// src/lib/segmentedValue.js — what the segmented date/time field is allowed to say.
//
// The component is in components/SegmentedInput.jsx; this is the half that decides. Split out
// because `compose` is the only function that tells the rest of the app anything, and the
// difference between '' and a value is the difference between "no date yet" and a date — so it
// is worth asking about directly, in a test, rather than through a rendered field.
// (Also literal: `node --test` cannot import a .jsx file at all.)

const pad2 = n => String(n).padStart(2, '0')

// ⚠️ A COMPLETE-BUT-IMPOSSIBLE DATE IS NOT A DATE. 31/02 fills every segment and is still not a
// day, and `new Date('2026-02-31')` does not refuse it — it silently rolls forward to March 3.
// So the check is a round trip through UTC, never a parse.
export function realDate(y, m, d) {
  if (m < 1 || m > 12 || d < 1) return false
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
}

// ── date: [dd, mm, yyyy] ⇄ 'YYYY-MM-DD' ─────────────────────────────────────────────────────
export const dateCompose = ([d, m, y]) => {
  if (!d || !m || (y || '').length !== 4) return ''
  const dn = Number(d), mn = Number(m), yn = Number(y)
  return realDate(yn, mn, dn) ? `${y}-${pad2(mn)}-${pad2(dn)}` : ''
}

export const dateExplode = v => (/^\d{4}-\d{2}-\d{2}/.test(v || '')
  ? [v.slice(8, 10), v.slice(5, 7), v.slice(0, 4)]
  : ['', '', ''])

// ── time: [HH, mm] ⇄ 'HH:MM', twenty-four hours ──────────────────────────────────────────────
export const timeCompose = ([h, m]) => {
  if (!h || !m) return ''
  const hn = Number(h), mn = Number(m)
  return hn <= 23 && mn <= 59 ? `${pad2(hn)}:${pad2(mn)}` : ''
}

// ⚠️ Postgres `time` comes back as '09:00:00'. Dropping the seconds is not cosmetic: the field
// re-seeds itself whenever `compose(parts) !== value`, so without this the boxes would say
// '09:00', compose to '09:00', disagree with the stored '09:00:00', and re-seed forever.
export const timeExplode = v => (/^\d{1,2}:\d{2}/.test(v || '')
  ? [pad2(Number(String(v).split(':')[0])), String(v).split(':')[1].slice(0, 2)]
  : ['', ''])

// ── A first digit that can start NO valid value completes the segment on its own ──────────────
//
// Found 2026-09-03 while sweeping every time box for the "11 → 01" bug: typing 9, then 4, 5
// into an hour — a person entering 09:45 — gave 23:05. The "9" sat waiting for a second digit,
// "94" accumulated, blur clamped it to the max (23) and the "5" fell into the minutes. A native
// <input type="time"> advances the moment you type a 3–9 into the hour, because no two-digit
// hour starts with them; likewise 6–9 in the minute, 4–9 in the day, 2–9 in the month.
//
// Returns the completed two-digit text when the single digit can only ever be that value, or
// null when a second digit is still possible. Pure so it can be tested without a DOM.
export function completeIfOnlyOption(digitText, max) {
  if (!/^\d$/.test(digitText || '')) return null
  const d = Number(digitText)
  // Could a second digit still produce a value <= max? "1" with max 23 → 10..19 possible.
  // "3" with max 23 → 30..39 all > 23 → this digit IS the value: "03".
  return d * 10 > max ? String(d).padStart(2, '0') : null
}
